import type { ChartData, PeriodParams } from '@charting-library'

import type { components } from '@/api/schema'
import { CHART_MAX_COUNTBACK } from '@/features/market/api/marketApi'
import type {
  ChartExchange,
  ChartResolution,
  ChartSymbolType,
  HistoryWindow,
} from '@/features/trading-chart/api/chartApi'
import { div, money, toChartNumber, type Money } from '@/lib/money'
import { knownWord } from '@/schemas/common'
import { NEPSE_TIMEZONE } from '@/schemas/trading-chart'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * Pure conversions from the `/charts/*` wire to the shapes the charting
 * library reads. Nothing here fetches or validates: `chartService` parses what
 * these return against `schemas/trading-chart.ts`.
 */

const EXCHANGE_PREFIX = /^NEPSE:/i

/**
 * The bare symbol mm-be looks up: `NEPSE:NABIL` → `NABIL`.
 *
 * The library may hand back a symbol in `EXCHANGE:SYMBOL` form (a typed
 * search, a saved layout), and mm-be knows only the bare one. Upper-cased so
 * "nabil" and "NABIL" share one cache entry; mm-be matches either.
 */
export const plainSymbol = (symbol: string): string =>
  symbol.trim().replace(EXCHANGE_PREFIX, '').trim().toUpperCase()

const DAILY_RESOLUTIONS = new Set(['1D', 'D'])
const INTRADAY_RESOLUTIONS: ReadonlySet<string> = new Set<ChartResolution>(['1', '5', '15', '60'])

/**
 * The bar size mm-be builds for a library resolution, or null for one it does not: daily under
 * either spelling, and 1, 5, 15 and 60 minutes (services/chart/resolutions.py). The library
 * combines every other size in the menu from these, so it should never ask for one.
 */
export const servedResolution = (resolution: string): ChartResolution | null => {
  const value = resolution.trim().toUpperCase()
  if (DAILY_RESOLUTIONS.has(value)) return '1D'
  return INTRADAY_RESOLUTIONS.has(value) ? (value as ChartResolution) : null
}

/**
 * The library's request window, as `history/` will accept it.
 *
 * Passed straight through, except where mm-be would answer a 400 for a window
 * it would otherwise serve. `countback` is capped at `CHART_MAX_COUNTBACK`:
 * a 12M view built from daily bars asks for tens of thousands, and a short
 * answer is fine because the library pages back with an earlier `to`.
 * `from` goes only when it is a real epoch before `to`: mm-be validates it
 * even when `countback` makes it unused.
 */
export const historyWindow = ({ from, to, countBack }: PeriodParams): HistoryWindow => {
  const end = Math.max(0, to)
  return {
    to: end,
    ...(countBack >= 1 ? { countback: Math.min(countBack, CHART_MAX_COUNTBACK) } : {}),
    ...(from >= 0 && from < end ? { from } : {}),
  }
}

/**
 * A wire decimal as Money, or `undefined` when it is not one.
 *
 * `money` throws on a non-decimal, and one malformed bar is not worth failing
 * the window over: the schema drops the row, and the rest still draw.
 */
const chartDecimal = (value: string | null | undefined): Money | undefined => {
  if (value === null || value === undefined) return undefined
  const raw = value.trim()
  if (raw === '' || !Number.isFinite(Number(raw))) return undefined
  return money(raw)
}

/** The same, as a chart number. */
const chartNumber = (value: string | null | undefined): number | undefined => {
  const decimal = chartDecimal(value)
  return decimal === undefined ? undefined : toChartNumber(decimal)
}

/** A bar before validation: a price that would not parse is left `undefined`. */
export interface BarCandidate {
  time: number
  open: number | undefined
  high: number | undefined
  low: number | undefined
  close: number | undefined
  volume?: number
}

/**
 * The column arrays zipped back into one bar each, oldest first as mm-be
 * sends them.
 *
 * `t` is in SECONDS — a daily bar's trading day at 00:00 UTC, an intraday
 * bar's start — and the library wants milliseconds. A null `v` (an index with no turnover recorded) leaves
 * `volume` out rather than drawing a zero.
 */
export const toBarCandidates = (series: Wire<'BarSeries'>): BarCandidate[] =>
  series.t.map((time, index) => {
    const volume = chartNumber(series.v[index])
    return {
      time: time * 1000,
      open: chartNumber(series.o[index]),
      high: chartNumber(series.h[index]),
      low: chartNumber(series.l[index]),
      close: chartNumber(series.c[index]),
      ...(volume === undefined ? {} : { volume }),
    }
  })

/**
 * The library's `HistoryMetadata` for one window.
 *
 * An empty window is `noData`, and carries `next_time` (the latest earlier
 * bar, in seconds) as `nextTime` in milliseconds when mm-be found one, so the
 * library jumps straight to it. mm-be sets `next_time` only for a window with
 * no `countback`.
 */
export const historyMeta = (series: Wire<'BarSeries'>): { noData: boolean; nextTime?: number } => {
  if (series.t.length > 0) return { noData: false }
  return series.next_time === null
    ? { noData: true }
    : { noData: true, nextTime: series.next_time * 1000 }
}

/**
 * mm-be's legacy `symbols` keys renamed to the library's.
 *
 * `minmovement` → `minmov`, and a `format` the library requires but the wire
 * never sends. `has_daily` is stated rather than left to the library's
 * default: weekly and monthly are built from it. A null
 * sector/industry (every index) is left out: the library's fields are
 * optional strings.
 */
export const toSymbolInfo = (wire: Wire<'ChartSymbol'>) => ({
  name: plainSymbol(wire.name),
  ticker: plainSymbol(wire.ticker),
  description: wire.description,
  type: wire.type,
  session: wire.session,
  exchange: wire.exchange,
  listed_exchange: wire.listed_exchange,
  timezone: NEPSE_TIMEZONE,
  format: 'price' as const,
  minmov: wire.minmovement,
  pricescale: wire.pricescale,
  has_intraday: wire.has_intraday,
  intraday_multipliers: wire.intraday_multipliers,
  has_daily: true,
  daily_multipliers: wire.daily_multipliers,
  has_weekly_and_monthly: wire.has_weekly_and_monthly,
  supported_resolutions: wire.supported_resolutions,
  volume_precision: 0,
  currency_code: wire.currency_code,
  ...(wire.sector === null ? {} : { sector: wire.sector }),
  ...(wire.industry === null ? {} : { industry: wire.industry }),
})

/**
 * A search hit as the library lists it. The library's row has no `full_name`;
 * it becomes the `ticker`, which is what `resolveSymbol` is then asked for.
 */
export const toSearchResult = (row: Wire<'SymbolMatch'>) => ({
  symbol: plainSymbol(row.symbol),
  ticker: plainSymbol(row.full_name),
  description: row.description,
  exchange: row.exchange,
  type: row.type,
})

/** The config less `supports_search` and `supports_group_request`, which the library does not read. */
export const toDatafeedConfig = (wire: Wire<'ChartConfig'>) => ({
  supports_marks: wire.supports_marks,
  supports_timescale_marks: wire.supports_timescale_marks,
  supports_time: wire.supports_time,
  exchanges: wire.exchanges,
  symbols_types: wire.symbols_types,
  supported_resolutions: wire.supported_resolutions,
})

const SYMBOL_TYPES: readonly ChartSymbolType[] = ['', 'stock', 'index']
const EXCHANGES: readonly ChartExchange[] = ['', 'NEPSE']

/** The search box's type filter, or all types for one mm-be does not know. */
export const searchSymbolType = (value: string): ChartSymbolType =>
  knownWord(SYMBOL_TYPES, value) ?? ''

/** The search box's exchange filter, or all exchanges for one mm-be does not know. */
export const searchExchange = (value: string): ChartExchange => knownWord(EXCHANGES, value) ?? ''

export const toLayoutMeta = (row: Wire<'LayoutSummary'>) => ({
  id: row.id,
  name: row.name,
  symbol: row.symbol,
  resolution: row.resolution,
  timestamp: row.timestamp,
})

/**
 * A layout save as mm-be takes it. The symbol is stored bare: mm-be caps it
 * at 10 characters, which an `EXCHANGE:` prefix alone would nearly spend.
 */
export const toLayoutWrite = (chart: ChartData): Wire<'LayoutWriteRequest'> => ({
  name: chart.name,
  symbol: plainSymbol(chart.symbol),
  resolution: chart.resolution,
  content: chart.content,
})

/**
 * The library's layout id (`string | number`) as mm-be's integer path param.
 * Throws for anything else: there is no layout it could name.
 */
export const layoutId = (id: string | number): number => {
  const value = typeof id === 'number' ? id : Number(id)
  if (!Number.isSafeInteger(value) || value < 1) throw new Error(`Not a layout id: ${String(id)}`)
  return value
}

/**
 * mm-be ranks `broker_net_top5`'s brokers, biggest net buyer first, but keys the lines by broker
 * code, and a JSON object keyed by numbers ("58", "6") comes back from `JSON.parse` in ascending
 * order, not in mm-be's. The ranking is the window's net kitta per broker, so it is summed back
 * here (ties by code, as mm-be breaks them) and the first line is the biggest buyer again.
 */
const RANKED_STUDIES: ReadonlySet<string> = new Set(['broker_net_top5'])

const windowNet = (values: readonly (number | string | null)[]): number =>
  values.reduce<number>((total, value) => (typeof value === 'number' ? total + value : total), 0)

/**
 * A study read as its pane draws it: one line per `series` key, named from `labels` (the key
 * itself when mm-be sent no name), each line cut or padded to `t` so every value has a session.
 * Values are passed as sent; `paneSeriesSchema` parses them.
 */
export const toPaneSeries = (wire: Wire<'StudySeries'>) => {
  const lines = Object.entries(wire.series).map(([key, values]) => ({
    key,
    label: wire.labels[key] ?? key,
    values: wire.t.map((_, index) => values[index] ?? null),
  }))
  if (RANKED_STUDIES.has(wire.study)) {
    lines.sort((a, b) => windowNet(b.values) - windowNet(a.values) || a.key.localeCompare(b.key))
  }
  return {
    times: wire.t,
    lines,
    estimated: wire.estimated,
    truncated: wire.truncated,
    lastSession: wire.to_date,
  }
}

/** The one line the average-trade-size pane draws: kitta per trade, our own name for it. */
const TRADE_SIZE_LINE = { key: 'trade_size', label: 'Kitta per trade' } as const

/**
 * Average trade size per session, `v ÷ n`: the kitta traded over the number of trades. A session
 * with either one unrecorded (`n` is null for the session still trading, for an index and for
 * intraday bars) or no trades at all is a gap, never 0.
 */
export const toTradeSizeSeries = (wire: Wire<'BarSeries'>) => ({
  times: wire.t,
  lines: [
    {
      ...TRADE_SIZE_LINE,
      values: wire.t.map((_, index) => {
        const volume = chartDecimal(wire.v[index])
        const trades = wire.n[index] ?? null
        return volume === undefined || trades === null || trades <= 0 ? null : div(volume, trades)
      }),
    },
  ],
  estimated: false,
  truncated: false,
  lastSession: null,
})
